import { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAnnouncementStore,
  AnnouncementFormValues,
  createAnnouncementSchema,
} from '@/pages/admin/store/announcementStore';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import { INSERT_WILDCARDS, UPDATE_WILDCARDS } from '@/graphql/mutations/wildcard/wildcard';
import {
  Enum_Wildcard_Status,
  InsertWildcardMutation,
  InsertWildcardMutationVariables,
} from '@/graphql/generated/graphql';
import { useToast } from '@/components/ui/use-toast';
import { FETCH_WILDCARDS } from '@/graphql/queries/wildcard/wildcard';
import uploadFile from '@/services/files/upload.service';

const convertToUKDate = (date: Date | string | undefined): Date => {
  if (!date) return dayjs().tz('Europe/London').toDate();
  return dayjs(date).tz('Europe/London').toDate();
};

export const useAnnouncementsForm = () => {
  const { formData, resetForm, editingId, page } = useAnnouncementStore();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditing = editingId !== null;
  const initialFormData = {
    ...formData,
    startDate: convertToUKDate(formData.startDate),
    endDate: convertToUKDate(formData.endDate),
    logo: formData.logo || (formData.logoId ? formData.logoId : ''),
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
    reset,
    trigger,
  } = useForm<AnnouncementFormValues>({
    resolver: zodResolver(createAnnouncementSchema(page)),
    defaultValues: initialFormData,
    mode: 'onChange',
  });

  const logoFile = watch('logo');
  const selectedSchoolIds = watch('schoolIds');

  const resetFormCompletely = () => {
    const defaultValues = {
      startDate: dayjs().tz('Europe/London').toDate(),
      endDate: dayjs().add(3, 'day').tz('Europe/London').toDate(),
      description: '',
      logo: '' as string,
      logoId: undefined,
      schoolIds: [],
    };
    resetForm();
    reset(defaultValues);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [insertWildcard] = useMutation<InsertWildcardMutation, InsertWildcardMutationVariables>(INSERT_WILDCARDS, {
    onCompleted: () => {
      toast({
        title: page?.form?.createSuccess || '',
        description: page?.form?.createSuccessDescription || '',
      });
      setIsSubmitting(false);
      resetFormCompletely();
    },
    onError: (error) => {
      toast({
        title: page?.form?.createError || '',
        description: `Error: ${error}`,
        variant: 'destructive',
      });
      setIsSubmitting(false);
      resetFormCompletely();
    },
    refetchQueries: [{ query: FETCH_WILDCARDS }],
  });

  const [updateWildcard] = useMutation(UPDATE_WILDCARDS, {
    onCompleted: () => {
      toast({
        title: page?.form?.updateSuccess || '',
        description: page?.form?.updateSuccessDescription || '',
      });
      setIsSubmitting(false);
      resetFormCompletely();
    },
    onError: (error) => {
      toast({
        title: page?.form?.updateError || '',
        description: `Error: ${error}`,
        variant: 'destructive',
      });
      setIsSubmitting(false);
      resetFormCompletely();
    },
    refetchQueries: [{ query: FETCH_WILDCARDS }],
  });

  const onSubmit = async (data: AnnouncementFormValues) => {
    setIsSubmitting(true);
    let logoId = data.logo as string | undefined;

    if (data.logo instanceof File && data.logo.size > 0) {
      const { data: uploadResponse } = await uploadFile(data.logo);
      logoId = uploadResponse?.[0]?.id;
    } else if (typeof data.logo === 'string' && data.logo.trim() !== '') {
      logoId = data.logo;
    } else {
      logoId = data.logoId;
    }

    if (!logoId) {
      toast({
        title: page?.form?.logoRequired || '',
        description: page?.form?.logoRequiredDescription || '',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }

    const startDate = convertToUKDate(data.startDate);
    const endDate = convertToUKDate(data.endDate);

    const variables = {
      id: useAnnouncementStore.getState().editingId,
      data: {
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD'),
        description: data.description,
        schools: data.schoolIds,
        status: Enum_Wildcard_Status.Draft,
        ctaText: 'Like',
        image: logoId,
        internal: false,
      },
    };

    const editingId = useAnnouncementStore.getState().editingId;

    if (editingId) {
      await updateWildcard({ variables });

      // After update, scroll to the updated announcement and center it in the viewport
      setTimeout(() => {
        const updatedElement = document.getElementById(`announcement-${editingId}`);
        if (updatedElement) {
          // Calculate position to center the element in the viewport
          const elementRect = updatedElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;

          // Scroll to the centered position
          window.scrollTo({
            top: middle,
            behavior: 'smooth',
          });
        }
      }, 500);
    } else {
      await insertWildcard({
        variables: { ...variables, data: { ...variables.data, title: page?.form?.defaultSchoolTitle || '' } },
      });
    }
  };

  useEffect(() => {
    const updatedFormData = {
      ...formData,
      startDate: convertToUKDate(formData.startDate),
      endDate: convertToUKDate(formData.endDate),
      logo: formData.logo || (formData.logoId ? formData.logoId : ''),
    };
    reset(updatedFormData);
  }, [formData, reset, trigger]);

  return {
    control,
    isSubmitting,
    errors,
    logoFile,
    fileInputRef,
    isEditing,
    handleSubmit,
    setValue,
    onSubmit,
    selectedSchoolIds,
    watch,
    trigger,
  };
};
